<template>
  <vx-card :title="budget.name || 'Budget Details'" class="mb-4 budget-view-card">
    <div class="flex justify-end gap-2 mb-4">
      <vs-button color="#2d3748" type="flat" icon-pack="feather" icon="icon-plus"
        @click="openPopup(null)">New Budget Line</vs-button>
      <vs-button color="success" type="flat" icon-pack="feather" icon="icon-download"
        @click="exportToExcel" :disabled="loading || !budget.budgetlines || budget.budgetlines.length === 0">
        Export Excel
      </vs-button>
    </div>
    <div v-if="loading" class="text-center my-4">Loading...</div>
    <div v-else>
      <div v-if="!budget.budgetlines || budget.budgetlines.length === 0" class="text-center text-red-500">
        No budget lines found.
      </div>
      <div v-else>
        <div class="overflow-x-auto" ref="tableContainer">
          <div v-for="(lines, group) in groupedBudgetLines" :key="'group-' + group">
            <table class="table-auto w-full border-collapse border border-gray-200 mb-4">
              <thead class="bg-gray-800 text-white">
                <tr>
                  <th class="border border-gray-300 px-4 py-1 text-left capitalize">{{ group }}</th>
                  <th v-for="(month, index) in months" :key="'header-' + month"
                    :class="{ 'current-month': isCurrentMonth(index) }"
                    class="border border-gray-300 px-4 py-1 text-center">
                    <big>{{ month.toUpperCase() }}</big>
                    <div class="flex">
                      <div class="w-1/3 text-center">
                        <span>BUDGET</span>
                      </div>
                      <div class="w-1/3 text-center">
                        <span>ACTUALS</span>
                      </div>
                      <div class="w-1/3 text-center">
                        <span>PCT</span>
                      </div>
                    </div>
                  </th>
                  <th class="border border-gray-300 px-4 py-1 text-center"><big>TOTAL YEAR</big>

                    <div class="flex">
                      <div class="w-1/3 text-center">
                        <span>BUDGET</span>
                      </div>
                      <div class="w-1/3 text-center">
                        <span>ACTUALS</span>
                      </div>
                      <div class="w-1/3 text-center">
                        <span>PCT</span>
                      </div>
                    </div>

                  </th>
                </tr>
              </thead>
              <tbody>


                <!-- Ligne des totaux -->
                <tr class="bg-gray-100" v-if="lines.length > 1">
                  <td class="border border-gray-300 px-4 py-1 text-left text-green-700">TOTAL</td>
                  <td v-for="month in monthsKeys" :key="'total-' + group + '-' + month"
                    class="border border-gray-300 px-4 py-1 text-center text-green-700">

                    <!-- Totaux EUR -->
                    <div class="flex normalized">
                      <!-- Totaux annuels EUR -->
                      <div class="w-1/3 text-center">
                        {{ toFi(calculateGroupTotal(lines, month, 'eur').budget) || '-' }}
                      </div>
                      <!-- Totaux annuels ACTUAL -->
                      <div class="w-1/3 text-center">
                        {{ toFi(calculateGroupTotal(lines, month, 'eur').actuals) || '-' }}
                      </div>
                      <!-- Totaux annuels PCT -->
                      <div class="w-1/3 text-center" v-html="renderArrowWithPercentage(
                        calculateGroupTotal(lines, month, 'eur').actuals,
                        calculateGroupTotal(lines, month, 'eur').budget, group
                      )">
                      </div>
                    </div>
                    <div class="flex small-italic">
                      <!-- Totaux annuels EUR -->
                      <div class="w-1/3 text-center">
                        {{ toFi(calculateGroupTotal(lines, month, 'vol').budget) }} {{ lines[0].unit }}
                      </div>
                      <!-- Totaux annuels ACTUAL -->
                      <div class="w-1/3 text-center">
                        {{ toFi(calculateGroupTotal(lines, month, 'vol').actuals) || '-' }} {{ lines[0].unit }}
                      </div>
                      <!-- Totaux annuels PCT-->
                      <div class="w-1/3 text-center" v-html="renderArrowWithPercentage(
                        calculateGroupTotal(lines, month, 'vol').actuals,
                        calculateGroupTotal(lines, month, 'vol').budget,group
                      )">
                      </div>
                    </div>
                  </td>
                  <td class="border border-gray-300 px-4 py-1 text-center text-green-700">
                    <!-- Totaux annuels EUR -->
                    <div class="flex normalized">
                      <div class="w-1/3 text-center">
                        {{ toFi(calculateAnnualTotal(lines, 'eur').budget) || '-' }}
                      </div>
                      <div class="w-1/3 text-center">
                        {{ toFi(calculateAnnualTotal(lines, 'eur').actuals) || '-' }}
                      </div>
                      <div class="w-1/3 text-center" v-html="renderArrowWithPercentage(
                        calculateAnnualTotal(lines, 'eur').actuals,
                        calculateAnnualTotal(lines, 'eur').budget, group
                      )">
                      </div>
                    </div>
                    <!-- Totaux annuels VOL -->
                    <div class="flex small-italic">
                      <div class="w-1/3 text-center">
                        {{ toFi(calculateAnnualTotal(lines, 'vol').budget) || '-' }} {{ lines[0].unit }}
                      </div>
                      <div class="w-1/3 text-center">
                        {{ toFi(calculateAnnualTotal(lines, 'vol').actuals) || '-' }} {{ lines[0].unit }}
                      </div>

                      <div class="w-1/3 text-center" v-html="renderArrowWithPercentage(
                        calculateAnnualTotal(lines, 'vol').actuals,
                        calculateAnnualTotal(lines, 'vol').budget,group
                      )">
                      </div>
                    </div>
                  </td>
                </tr>

                <!-- Lignes individuelles -->
                <tr v-for="line in lines" :key="line.id" class="hover:bg-gray-50">
                  <td class="border border-gray-300 px-4 py-1 text-left font-medium" @click="openPopup(line)">{{
                    line.name }}
                  </td>
                  <td v-for="(month, index) in monthsKeys" :key="'line-' + line.id + '-' + month"
                    class="border border-gray-300 px-4 py-1 text-center">



                    <div class="flex normalized" v-if="line.actuals && line.actuals.eur">
                      <!-- BUDGET -->
                      <div class="w-1/3 text-center">
                        {{ line[`${month}_eur`] ? toFi(line[`${month}_eur`]) : '-' }}
                      </div>
                      <!-- ACTUAL -->
                      <div class="w-1/3 text-center">
                        {{ (line.actuals && line.actuals.eur && line.actuals.eur[index + 1]) ?
                          toFi(line.actuals.eur[index + 1]) : '-' }}
                      </div>
                      <!-- PCT -->
                      <div class="w-1/3 text-center" v-html="renderArrowWithPercentage(
                        line.actuals && line.actuals.eur ? line.actuals.eur[index + 1] : 0,
                        line[`${month}_eur`] || '-', group
                      )">
                      </div>
                    </div>


                    <div class="flex small-italic">
                      <!-- BUDGET -->
                      <div class="w-1/3 text-center">
                        {{ line[`${month}_vol`] ? toFi(line[`${month}_vol`]) : '-' }} {{ lines[0].unit }}
                      </div>
                      <!-- ACTUAL -->
                      <div class="w-1/3 text-center">
                        {{ (line.actuals && line.actuals.vol && line.actuals.vol[index + 1]) ?
                          toFi(line.actuals.vol[index + 1]) : '-' }} {{ lines[0].unit }}
                      </div>
                      <!-- PCT -->
                      <div class="w-1/3 text-center" v-if="(line.actuals && !line.actuals.eur)" v-html="renderArrowWithPercentage(
                        line.actuals && line.actuals.vol ? line.actuals.vol[index + 1] : 0,
                        line[`${month}_vol`] || '-',group
                      )">
                      </div>
                    </div>

                  </td>

                  <td class="border border-gray-300 px-4 py-1 text-center font-semibold">

                    <div class="flex normalized" v-if="line.actuals && line.actuals.eur">
                      <!-- BUDGET -->
                      <div class="w-1/3 text-center">
                        {{ calculateAnnualTotal([line], 'eur').budget ? toFi(calculateAnnualTotal([line], 'eur').budget)
                          : '-' }}
                      </div>
                      <!-- ACTUAL -->
                      <div class="w-1/3 text-center">
                        {{ calculateAnnualTotal([line], 'eur').actuals ? toFi(calculateAnnualTotal([line],
                          'eur').actuals) : '-' }}
                      </div>
                      <!-- PCT -->
                      <div class="w-1/3 text-center" v-html="renderArrowWithPercentage(
                        calculateAnnualTotal([line], 'eur').actuals || '-',
                        calculateAnnualTotal([line], 'eur').budget || '-',group
                      )">
                      </div>
                    </div>
                    <div class="flex small-italic">
                      <!-- BUDGET -->
                      <div class="w-1/3 text-center">
                        {{ calculateAnnualTotal([line], 'vol').budget ? toFi(calculateAnnualTotal([line], 'vol').budget)
                          : '-' }} {{ lines[0].unit }}
                      </div>
                      <!-- ACTUAL -->
                      <div class="w-1/3 text-center">
                        {{ calculateAnnualTotal([line], 'vol').actuals ? toFi(calculateAnnualTotal([line],
                          'vol').actuals) : '-' }} {{ lines[0].unit }}
                      </div>
                      <!-- PCT -->
                      <div class="w-1/3 text-center" v-html="renderArrowWithPercentage(
                        calculateAnnualTotal([line], 'vol').actuals || '-',
                        calculateAnnualTotal([line], 'vol').budget || '-',group
                      )">
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <!-- Popup fullscreen -->
    <vs-popup title="New budget line" fullscreen :active.sync="isPopupOpen">
      <template v-slot:activator="{ on, attrs }"></template>
      <div>

        <!-- Content -->
        <div class="popup-content grid grid-cols-12 gap-4">
          <!-- Informations générales -->
          <div class="col-span-12 md:col-span-6">
            <h4 style="
      font-size: 12px;
      font-weight: bold;
      margin-top: 20px;
      border-bottom: 2px solid #444;
      padding-bottom: 5px;
      color: #444;
    ">
              General Information
            </h4>
            <vs-input label="Name" v-model="formData.name" class="mt-2 w-full" placeholder="" />
            <vs-input label="Group" v-model="formData.group" class="mt-2 w-full" placeholder="" />
            <vs-input label="Year" v-model="formData.year" class="mt-2 w-full" type="number" placeholder="" />
            <vs-input label="Unit" v-model="formData.unit" class="mt-2 w-full" placeholder="pcs, m3, etc." />
          </div>

          <!-- Requêtes SQL -->
          <div class="col-span-12 md:col-span-6">
            <h4 style="
      font-size: 12px;
      font-weight: bold;
      margin-top: 20px;
      border-bottom: 2px solid #444;
      padding-bottom: 5px;
      color: #444;
    ">
              SQL for actuals
            </h4>
            <vs-textarea style="height: auto" rows="3" class="w-full mt-8" label="SQL EUR (Actuals)"
              v-model="formData.sqleur" />
            <vs-textarea style="height: auto" rows="3" class="w-full mt-6" label="SQL UNIT (Actuals)"
              v-model="formData.sqlron" />
          </div>

          <!-- Montants mensuels -->
          <div class="col-span-12" v-if="formData.unit">
            <h4 style="
    font-size: 12px;
    font-weight: bold;
    margin-top: 20px;
    border-bottom: 2px solid #444;
    padding-bottom: 5px;
    color: #444;
  ">
              Totals and Monthly Budget
            </h4>

            <!-- Totals -->
            <div style="margin-top: 10px; display: flex; gap: 20px;">
              <div style="flex: 1;">
                <vs-input label="Total Annual EUR" v-model="totalAnnualEUR" type="number" step="0.01" style="
          width: 100%;
          padding: 2px;
          border: 1px solid #ddd;
          border-radius: 5px;
          font-size: 14px;
        " />
              </div>
              <div style="flex: 1;">
                <vs-input :label="'Total Annual ' + (formData.unit || '')" v-model="totalAnnualVOL" type="number"
                  step="0.01" style="
          width: 100%;
          padding: 2px;
          border: 1px solid #ddd;
          border-radius: 5px;
          font-size: 14px;
        " />
              </div>
            </div>

            <!-- Buttons for Distribution -->
            <div style="margin-top: 10px; display: flex; gap: 20px;">
              <vs-button @click="distributeTotal('eur')" color="primary" class="w-full" style="font-size: 12px;">
                Distribute Total Annual EUR
              </vs-button>
              <vs-button @click="distributeTotal('vol')" color="primary" class="w-full" style="font-size: 12px;">
                Distribute Total Annual {{ this.formData.unit }}
              </vs-button>
            </div>

            <!-- Monthly Budget -->
            <table style="
    width: 100%;
    border-collapse: collapse;
    margin-top: 20px;
    font-size: 12px;
  ">
              <thead>
                <tr>
                  <th style="
          text-align: left;
          padding: 10px;
          background-color: #f8f8f8;
          color: #333;
          font-weight: bold;
          border-bottom: 2px solid #ddd;
        ">
                    MONTH
                  </th>
                  <th style="
          text-align: left;
          padding: 1px;
          background-color: #f8f8f8;
          color: #333;
          font-weight: bold;
          border-bottom: 2px solid #ddd;
        ">
                    EUR
                  </th>
                  <th style="
          text-align: left;
          padding: 1px;
          background-color: #f8f8f8;
          color: #333;
          font-weight: bold;
          border-bottom: 2px solid #ddd;
        ">
                    {{ this.formData.unit }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="month in monthsKeys" :key="month"
                  style="border-bottom: 1px solid #eee; background-color: #fff;">
                  <td style="padding: 1px; text-transform: capitalize; color: #555;">
                    {{ month.toUpperCase() }}
                  </td>
                  <td style="padding: 0px;">
                    <vs-input v-model="formData[`${month}_eur`]" type="number" step="0.01" placeholder="0.00"
                      @input="updateAnnualTotal('eur')" style="
              width: 100%;
              padding: 0px;
              border: 1px solid #ddd;
              border-radius: 5px;
              font-size: 14px;
            " />
                  </td>
                  <td style="padding: 0px;">
                    <vs-input v-model="formData[`${month}_vol`]" type="number" step="0.01" placeholder="0.00"
                      @input="updateAnnualTotal('vol')" style="
              width: 100%;
              padding: 0px;
              border: 1px solid #ddd;
              border-radius: 5px;
              font-size: 14px;
            " />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Footer -->
        <div class="popup-footer flex justify-end gap-4 p-4">
          <vs-button @click="saveLine" color="success">Save</vs-button>
        </div>
      </div>
    </vs-popup>
  </vx-card>

</template>


<script>
import moment from "moment";
import axios from "@/axios.js";
import XLSX_STYLE from "xlsx-js-style";
export default {
  data() {
    return {
      totalAnnualEUR: 0,
      totalAnnualVOL: 0,
      currentLine: null,
      formData: {
        name: "",
        group: "",
        year: new Date().getFullYear(),
        sqleur: "",
        sqlron: "",
      },
      isPopupOpen: false,
      loading: false,
      budget: {}, // Données du budget
      months: [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ],
      monthsKeys: [
        "jan", "feb", "mar", "apr", "may", "jun",
        "jul", "aug", "sep", "oct", "nov", "dec"
      ]
    };
  },
  computed: {
    // Regroupe les lignes par "group"
    groupedBudgetLines() {
      return this.budget.budgetlines.reduce((groups, line) => {
        if (!groups[line.group]) {
          groups[line.group] = [];
        }
        groups[line.group].push(line);
        return groups;
      }, {});
    }
  },
  methods: {
    renderArrowWithPercentage(actual, budget, group) {
      const actualNum = parseFloat(actual) || 0;
      const budgetNum = parseFloat(budget) || 0;

      if (budgetNum === 0) return '-';

      const percentage = ((actualNum / budgetNum) * 100).toFixed(0);

      if (percentage == 0) return '-'; // Si le pourcentage est exactement 0, on retourne '-'


      let arrow = null
      let color = null
      if (group === '5. Expenses' || group === '1. Transport') {
        arrow = percentage >= 100 ? '↑' : '↓';
        color = percentage >= 100 ? 'red' : 'green';

      }
      else {
        arrow = percentage >= 100 ? '↑' : '↓';
        color = percentage >= 100 ? 'green' : 'red';

      }
      return `<span style="color: ${color};">${percentage}% ${arrow}</span>`;
    },
    toFi(value) {
      if (!isNaN(parseFloat(value))) {
        return new Intl.NumberFormat('en-EN', { maximumFractionDigits: 0 }).format(parseFloat(value));
      } else {
        return '-';
      }
    },
    updateAnnualTotal(type) {
      const total = this.monthsKeys.reduce((sum, month) => {
        const value = parseFloat(this.formData[`${month}_${type}`]) || 0;
        return sum + value;
      }, 0);

      if (type === "eur") {
        this.totalAnnualEUR = total.toFixed(0);
      } else if (type === "vol") {
        this.totalAnnualVOL = total.toFixed(0);
      }
    },

    // Répartit la valeur totale entre tous les mois
    distributeTotal(type) {
      const total = type === "eur" ? this.totalAnnualEUR : this.totalAnnualVOL;
      const monthlyValue = (parseFloat(total) / 12).toFixed(0);

      this.monthsKeys.forEach((month) => {
        this.$set(this.formData, `${month}_${type}`, monthlyValue);
      });

      this.updateAnnualTotal(type); // Met à jour la somme annuelle
    },
    openPopup(line) {
      /*this.totalAnnualEUR = 0
      this.totalAnnualVOL = 0*/
      this.currentLine = line;
      if (line) {
        this.formData = { ...line }; // Remplit les champs pour l'édition
        this.updateAnnualTotal("eur")
        this.updateAnnualTotal("vol")
      } else {
        this.updateAnnualTotal("eur")
        this.updateAnnualTotal("vol")
        this.formData = {
          name: "",
          group: "",
          year: new Date().getFullYear(),
          sqleur: "",
          sqlron: "",
        };
      }
      this.isPopupOpen = true;
    },
    saveLine() {
      const payload = {
        name: this.formData.name,
        group: this.formData.group,
        year: this.formData.year,
        unit: this.formData.unit,
        idbudget: this.$route.params.idbudget,
        sqleur: this.formData.sqleur || "",
        sqlron: this.formData.sqlron || "",
      };

      // Ajout des montants mensuels
      this.monthsKeys.forEach((month) => {
        payload[`${month}_eur`] = this.formData[`${month}_eur`] || 0;
        payload[`${month}_vol`] = this.formData[`${month}_vol`] || 0;
      });

      if (this.currentLine) {
        // Mise à jour d'une ligne existante
        axios
          .put(`/budgetline/${this.currentLine.id}`, payload)
          .then(() => {
            this.$vs.notify({
              title: "Succès",
              text: "Ligne de budget mise à jour avec succès.",
              color: "success",
              position: "top-right",
            });
            this.isPopupOpen = false;
            this.fetchbudget();
          })
          .catch((error) => {
            console.error("Erreur lors de la mise à jour de la ligne :", error);
            this.$vs.notify({
              title: "Erreur",
              text: "Une erreur est survenue lors de la mise à jour.",
              color: "danger",
              position: "top-right",
            });
          });
      } else {
        // Création d'une nouvelle ligne
        axios
          .post("/budgetline", payload)
          .then(() => {
            this.$vs.notify({
              title: "Succès",
              text: "Ligne de budget ajoutée avec succès.",
              color: "success",
              position: "top-right",
            });
            this.isPopupOpen = false;
            this.fetchbudget();
          })
          .catch((error) => {
            console.error("Erreur lors de l'ajout de la ligne :", error);
            this.$vs.notify({
              title: "Erreur",
              text: "Une erreur est survenue lors de l'ajout de la ligne.",
              color: "danger",
              position: "top-right",
            });
          });
      }
    },
    getComparisonIcon(actuals, budget) {
      const actualsNum = parseFloat(actuals) || 0; // Convertir en nombre ou 0 par défaut
      const budgetNum = parseFloat(budget) || 0;

      if (actualsNum >= budgetNum) {
        //alert(`${actualsNum} >= ${budgetNum} : TOP`);
        return {
          icon: "ActivityIcon",
          color: "text-top", // Couleur HTML pour vert
        };
      } else {
        //alert(`${actualsNum} < ${budgetNum} : BAD`);
        return {
          icon: "AlertTriangleIcon",
          color: "text-bad", // Couleur HTML pour rouge
        };
      }
    },
    scrollToCurrentMonth() {
      const currentMonthIndex = moment().month(); // Mois actuel (0 = janvier)
      const container = this.$refs.tableContainer;
      if (!container) return;

      // Ajouter un délai
      setTimeout(() => {
        // Trouver la première colonne pour calculer dynamiquement la largeur
        const firstColumn = container.querySelector("th");
        if (!firstColumn) return;

        const columnWidth = firstColumn.offsetWidth; // Largeur réelle d'une colonne
        const scrollPosition = currentMonthIndex * columnWidth;

        container.scrollTo({
          left: scrollPosition,
          behavior: "smooth", // Défilement fluide
        });
      }, 1000); // Délai de 300 ms
    },
    isCurrentMonth(index) {
      const currentMonth = moment().month(); // Mois actuel (0 = janvier)
      return index === currentMonth;
    },
    // Calculer le total pour un groupe et un type (EUR ou Volume)
    calculateAnnualTotal(lines, type) {
      const totals = lines.reduce(
        (acc, line) => {
          // Vérification pour les actuals
          let actualsForType = line.actuals && line.actuals[type];
          let actualsTotal = 0;
          if (actualsForType) {
            actualsTotal = Object.values(actualsForType).reduce(
              (sum, value) => sum + parseFloat(value || 0),
              0
            );
          }

          // Calcul des budgets
          const budgetTotal = this.monthsKeys.reduce(
            (sum, month) => sum + parseFloat(line[`${month}_${type}`] || 0),
            0
          );

          // Accumuler les totaux
          acc.actuals += actualsTotal;
          acc.budget += budgetTotal;

          return acc;
        },
        { actuals: 0, budget: 0 }
      );

      // Si les totaux sont 0, retourne '-'
      return {
        actuals: totals.actuals === 0 ? '-' : totals.actuals.toFixed(0),
        budget: totals.budget === 0 ? '-' : totals.budget.toFixed(0),
      };
    },
    calculateGroupTotal(lines, monthKey, type) {
      const monthIndex = this.monthsKeys.indexOf(monthKey) + 1; // Convertir "jan" en 1, "feb" en 2, etc.
      const totals = lines.reduce(
        (acc, line) => {
          // Vérification explicite des actuals
          const actualsForType = line.actuals && line.actuals[type];
          const actualValue =
            actualsForType && actualsForType[monthIndex]
              ? parseFloat(actualsForType[monthIndex]) || 0
              : 0;

          // Gestion des budgets
          const budgetValue = parseFloat(line[`${monthKey}_${type}`] || 0);

          // Ajouter aux totaux
          acc.actuals += actualValue;
          acc.budget += budgetValue;

          return acc;
        },
        { actuals: 0, budget: 0 } // Initialisation des totaux
      );

      return {
        actuals: totals.actuals === 0 ? '-' : totals.actuals.toFixed(0),
        budget: totals.budget === 0 ? '-' : totals.budget.toFixed(0),
      };
    },
    async fetchbudget() {
      this.loading = true;
      try {
        const response = await axios.get("budget/data/" + this.$route.params.idbudget);
        this.budget = response.data;
        this.$nextTick(() => {
          this.scrollToCurrentMonth(); // Appeler après le rendu
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des budgets:", error);
      } finally {
        this.loading = false;
      }
    },
    // Fonction helper pour calculer le pourcentage sans flèches (pour Excel) - retourne un nombre ou ""
    calculatePercentageOnly(actual, budget) {
      const actualNum = parseFloat(actual) || 0;
      const budgetNum = parseFloat(budget) || 0;

      if (budgetNum === 0) return "";

      const percentage = (actualNum / budgetNum) * 100;
      return isNaN(percentage) ? "" : percentage;
    },
    // Fonction helper pour formater un nombre - retourne un nombre ou ""
    formatNumber(value) {
      if (value === "" || value === "-" || value === null || value === undefined) return "";
      const num = parseFloat(value);
      if (isNaN(num)) return "";
      return num;
    },
    exportToExcel() {
      if (!this.budget.budgetlines || this.budget.budgetlines.length === 0) {
        this.$vs.notify({
          title: "Erreur",
          text: "Aucune donnée à exporter.",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      // Créer un nouveau workbook
      const wb = XLSX_STYLE.utils.book_new();

      // Pour chaque groupe, créer une feuille
      Object.keys(this.groupedBudgetLines).forEach((groupName) => {
        const lines = this.groupedBudgetLines[groupName];
        const wsData = [];

        // En-tête principal avec fusion des cellules pour les mois
        const headerRow = [groupName];
        this.months.forEach((month) => {
          headerRow.push(month.toUpperCase(), null, null); // null pour les cellules fusionnées
        });
        headerRow.push("TOTAL YEAR", null, null); // null pour les cellules fusionnées
        wsData.push(headerRow);

        // Sous-en-têtes
        const subHeaderRow = [""];
        this.months.forEach(() => {
          subHeaderRow.push("BUDGET", "ACTUALS", "PCT");
        });
        subHeaderRow.push("BUDGET", "ACTUALS", "PCT");
        wsData.push(subHeaderRow);

        // Ligne de totaux du groupe EUR (si plusieurs lignes)
        if (lines.length > 1) {
          const totalEurRow = ["TOTAL (EUR)"];
          this.monthsKeys.forEach((monthKey) => {
            const eurTotal = this.calculateGroupTotal(lines, monthKey, "eur");
            totalEurRow.push(
              this.formatNumber(eurTotal.budget),
              this.formatNumber(eurTotal.actuals),
              this.calculatePercentageOnly(
                eurTotal.actuals !== "-" ? eurTotal.actuals : 0,
                eurTotal.budget !== "-" ? eurTotal.budget : 0
              )
            );
          });
          // Total annuel EUR du groupe
          const annualEurTotal = this.calculateAnnualTotal(lines, "eur");
          totalEurRow.push(
            this.formatNumber(annualEurTotal.budget),
            this.formatNumber(annualEurTotal.actuals),
            this.calculatePercentageOnly(
              annualEurTotal.actuals !== "-" ? annualEurTotal.actuals : 0,
              annualEurTotal.budget !== "-" ? annualEurTotal.budget : 0
            )
          );
          wsData.push(totalEurRow);

          // Ligne de totaux du groupe VOL (si unité disponible)
          if (lines[0] && lines[0].unit) {
            const totalVolRow = [`TOTAL (${lines[0].unit})`];
            this.monthsKeys.forEach((monthKey) => {
              const volTotal = this.calculateGroupTotal(lines, monthKey, "vol");
              totalVolRow.push(
                this.formatNumber(volTotal.budget),
                this.formatNumber(volTotal.actuals),
                this.calculatePercentageOnly(
                  volTotal.actuals !== "-" ? volTotal.actuals : 0,
                  volTotal.budget !== "-" ? volTotal.budget : 0
                )
              );
            });
            // Total annuel VOL du groupe
            const annualVolTotal = this.calculateAnnualTotal(lines, "vol");
            totalVolRow.push(
              this.formatNumber(annualVolTotal.budget),
              this.formatNumber(annualVolTotal.actuals),
              this.calculatePercentageOnly(
                annualVolTotal.actuals !== "-" ? annualVolTotal.actuals : 0,
                annualVolTotal.budget !== "-" ? annualVolTotal.budget : 0
              )
            );
            wsData.push(totalVolRow);
          }
        }

        // Lignes individuelles
        lines.forEach((line) => {
          // Ligne EUR
          if (line.actuals && line.actuals.eur) {
            const eurRow = [`${line.name} (EUR)`];
            this.monthsKeys.forEach((monthKey, index) => {
              const monthEur = line[`${monthKey}_eur`] || 0;
              const monthActualEur =
                line.actuals && line.actuals.eur && line.actuals.eur[index + 1]
                  ? line.actuals.eur[index + 1]
                  : 0;

              eurRow.push(
                this.formatNumber(monthEur),
                this.formatNumber(monthActualEur),
                this.calculatePercentageOnly(monthActualEur, monthEur)
              );
            });
            // Total annuel EUR de la ligne
            const annualEur = this.calculateAnnualTotal([line], "eur");
            eurRow.push(
              this.formatNumber(annualEur.budget),
              this.formatNumber(annualEur.actuals),
              this.calculatePercentageOnly(
                annualEur.actuals !== "-" ? annualEur.actuals : 0,
                annualEur.budget !== "-" ? annualEur.budget : 0
              )
            );
            wsData.push(eurRow);
          }

          // Ligne VOL (si unité disponible)
          if (line.unit) {
            const volRow = [`${line.name} (${line.unit})`];
            this.monthsKeys.forEach((monthKey, index) => {
              const monthVol = line[`${monthKey}_vol`] || 0;
              const monthActualVol =
                line.actuals && line.actuals.vol && line.actuals.vol[index + 1]
                  ? line.actuals.vol[index + 1]
                  : 0;

              volRow.push(
                this.formatNumber(monthVol),
                this.formatNumber(monthActualVol),
                this.calculatePercentageOnly(monthActualVol, monthVol)
              );
            });
            // Total annuel VOL
            const annualVol = this.calculateAnnualTotal([line], "vol");
            volRow.push(
              this.formatNumber(annualVol.budget),
              this.formatNumber(annualVol.actuals),
              this.calculatePercentageOnly(
                annualVol.actuals !== "-" ? annualVol.actuals : 0,
                annualVol.budget !== "-" ? annualVol.budget : 0
              )
            );
            wsData.push(volRow);
          }
        });

        // Créer la feuille de calcul
        const ws = XLSX_STYLE.utils.aoa_to_sheet(wsData);

        // Ajouter les cellules fusionnées pour les en-têtes de mois
        // La première ligne (index 0) contient les noms de mois
        // Chaque mois occupe 3 colonnes (BUDGET, ACTUALS, PCT)
        const merges = [];
        let colIndex = 1; // Commence après la colonne A (groupName)

        // Fusionner les cellules pour chaque mois (JAN sur BCD, FEB sur EFG, etc.)
        this.months.forEach(() => {
          merges.push({
            s: { r: 0, c: colIndex }, // Début de la fusion (ligne 0, colonne colIndex)
            e: { r: 0, c: colIndex + 2 } // Fin de la fusion (ligne 0, colonne colIndex + 2)
          });
          colIndex += 3;
        });

        // Fusionner les cellules pour "TOTAL YEAR"
        merges.push({
          s: { r: 0, c: colIndex },
          e: { r: 0, c: colIndex + 2 }
        });

        ws["!merges"] = merges;

        // Appliquer le style (centré et gras) aux cellules des mois et TOTAL YEAR
        colIndex = 1;
        this.months.forEach((month) => {
          const cellAddress = XLSX_STYLE.utils.encode_cell({ r: 0, c: colIndex });
          if (!ws[cellAddress]) {
            ws[cellAddress] = { v: month.toUpperCase(), t: "s" };
          }
          // Appliquer le style : centré et gras
          ws[cellAddress].s = {
            alignment: { horizontal: "center", vertical: "center" },
            font: { bold: true }
          };
          colIndex += 3;
        });

        // Style pour "TOTAL YEAR"
        const totalYearCellAddress = XLSX_STYLE.utils.encode_cell({ r: 0, c: colIndex });
        if (ws[totalYearCellAddress]) {
          ws[totalYearCellAddress].s = {
            alignment: { horizontal: "center", vertical: "center" },
            font: { bold: true }
          };
        }

        // S'assurer que tous les nombres sont bien des nombres (pas du texte)
        // Parcourir toutes les cellules de données (à partir de la ligne 2)
        const range = XLSX_STYLE.utils.decode_range(ws["!ref"]);
        for (let R = 2; R <= range.e.r; R++) { // Commencer à la ligne 2 (après les en-têtes)
          // Vérifier si c'est une ligne TOTAL (première colonne contient "TOTAL")
          const firstCellAddress = XLSX_STYLE.utils.encode_cell({ r: R, c: 0 });
          const firstCell = ws[firstCellAddress];
          const isTotalRow = firstCell && firstCell.v &&
            (typeof firstCell.v === "string" && firstCell.v.toUpperCase().includes("TOTAL"));

          for (let C = 0; C <= range.e.c; C++) { // Commencer à la colonne A pour inclure le label
            const cellAddress = XLSX_STYLE.utils.encode_cell({ r: R, c: C });
            const cell = ws[cellAddress];
            if (!cell) continue;

            // Appliquer le style gras aux lignes TOTAL
            if (isTotalRow) {
              if (!cell.s) cell.s = {};
              if (!cell.s.font) cell.s.font = {};
              cell.s.font.bold = true;
            }

            // Si c'est déjà un nombre, on continue pour les autres colonnes
            if (cell.t === "n" && C > 0) continue;

            // Si c'est null ou undefined, on le laisse tel quel
            if (cell.v === null || cell.v === undefined) continue;

            // Si c'est une chaîne, essayer de convertir en nombre (sauf pour la première colonne)
            if (typeof cell.v === "string" && C > 0) {
              const num = parseFloat(cell.v);
              if (!isNaN(num) && isFinite(num)) {
                cell.v = num;
                cell.t = "n"; // Type nombre
              }
            }
            // Si c'est un nombre mais pas marqué comme tel
            else if (typeof cell.v === "number" && C > 0) {
              cell.t = "n";
            }
          }
        }

        // Ajuster la largeur des colonnes
        const colWidths = [{ wch: 30 }]; // Première colonne (nom)
        for (let i = 0; i < this.months.length * 3 + 3; i++) {
          colWidths.push({ wch: 12 });
        }
        ws["!cols"] = colWidths;

        // Ajouter la feuille au workbook avec un nom de feuille valide
        // Nettoyer le nom pour enlever les caractères interdits : \ / ? * [ ]
        let sheetName = groupName
          .replace(/[:\\\/\?\*\[\]]/g, "_") // Remplacer les caractères interdits par _
          .substring(0, 31) || "Sheet"; // Excel limite à 31 caractères
        XLSX_STYLE.utils.book_append_sheet(wb, ws, sheetName);
      });

      // Générer le nom du fichier
      const fileName = `${this.budget.name || "Budget"}_${moment().format("YYYY-MM-DD_HH-mm-ss")}.xlsx`;

      // Télécharger le fichier
      XLSX_STYLE.writeFile(wb, fileName);

      this.$vs.notify({
        title: "Succès",
        text: "Export Excel réussi.",
        color: "success",
        position: "top-right",
      });
    }
  },
  async created() {

    const roles = localStorage.getItem("login-roles");
    if (roles) {
      const rolesLower = roles.toLowerCase();
      if (rolesLower.includes("accountant")) {
        this.$router.push({ path: "/outlay/outlay-list" });
      }
      if (rolesLower.includes("admin")) {
        await this.fetchbudget();
      } else {
        this.$vs.notify({
          title: "Denied",
          text: "Access to the Finance page is reserved for administrators",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "danger",
          position: "bottom-right",
          time: 8000,
        });
        this.$router.push({ path: "/dashboard/analytics" });
      }
    } else {
      this.$vs.notify({
        title: "Erreur",
        text: "Roluri de autentificare lipsă.",
        iconPack: "feather",
        icon: "icon-alert-circle",
        color: "danger",
        position: "bottom-right",
        time: 8000,
      });
      this.$router.push({ path: "/login" });
    }
  }
};
</script>

<style scoped>
.small-italic {
  font-size: 78%;
  /* Réduction de la taille de police */
  font-style: italic;
  /* Style en italique */
  font-weight: 100;
}

.normalized {
  border: 1px dashed rgba(0, 0, 0, 0.107);
  /* Bordure légère globale */
  border-radius: 4px;
  /* Coins arrondis */
  padding: 1px;
  /* Espacement interne */
  display: flex;
  gap: 3px;
  /* Espacement entre les enfants */
}


.current-month {
  color: #ddadad;
}

/* Conteneur principal */
.overflow-x-auto {
  overflow-x: scroll;
  overflow-y: auto;
  white-space: nowrap;
  /* Empêche les colonnes de se casser */
  border-radius: 0;
  background-color: #ffffff;
  margin-top: 16px;
  margin-bottom: 0 !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  padding-bottom: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  max-height: calc(100vh - 140px);
  /* Force l'affichage de la barre de défilement horizontale */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f7fafc;
  /* Supprime tout espace en dessous */
  display: block;
  box-sizing: border-box;
  /* S'assurer que le fond blanc couvre tout jusqu'à la scrollbar */
  position: relative;
}

/* Force l'affichage de la barre de défilement horizontale sur WebKit */
.overflow-x-auto::-webkit-scrollbar {
  height: 12px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #f7fafc;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 6px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

/* Supprimer l'espace en dessous du dernier tableau */
.overflow-x-auto > div:last-child > table {
  margin-bottom: 0 !important;
}

/* Supprimer tout espace en dessous du conteneur et de son contenu */
.overflow-x-auto > div {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

.overflow-x-auto > div:last-child {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

/* Supprimer l'espace du conteneur parent et utiliser flex */
.vx-card > div:last-child {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.vx-card > div:last-child > div:last-child {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

/* Supprimer le padding-bottom uniquement pour le conteneur avec scrollbar */
.vx-card > div:last-child > div:last-child > div.overflow-x-auto {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

/* Supprimer l'espace après le conteneur overflow-x-auto */
div:has(> .overflow-x-auto) {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}

/* Supprimer le padding-bottom du vx-card body */
.vx-card .vx-card__body {
  padding-bottom: 0 !important;
}

/* S'assurer que le conteneur parent n'a pas d'espace en bas */
.vx-card {
  padding-bottom: 0 !important;
  overflow: visible;
}

/* Cibler spécifiquement tous les divs enfants du vx-card pour supprimer le padding */
.vx-card > div {
  padding-bottom: 0 !important;
}

/* Cibler le dernier div du vx-card (body) */
.vx-card > div:last-child {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}

/* Cibler spécifiquement la carte budget pour supprimer tout padding en bas */
.budget-view-card {
  padding-bottom: 0 !important;
}

.budget-view-card .vx-card__body,
.budget-view-card > div {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}

/* S'assurer que le conteneur avec scrollbar s'étend jusqu'en bas */
.budget-view-card .overflow-x-auto {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

/* Cibler spécifiquement le conteneur avec les tables pour qu'il s'étende jusqu'en bas */
.vx-card > div:last-child > div:last-child > div.overflow-x-auto {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
  /* S'étend jusqu'au bord pour couvrir l'espace gris */
  position: relative;
  z-index: 1;
}

/* Tableau */
.table-auto {
  width: 100%;
  border-collapse: collapse;
  font-family: "Open Sans", sans-serif;
  font-weight: 450;
  /* Fine */
  font-size: 12px;
  line-height: 1.3;
}

.table-auto th,
.table-auto td {
  width: 260px;
  /* Largeur fixe */
  max-width: 260px;
  /* Limite la largeur */
  min-width: 260px;
  /* Limite la largeur */
  text-align: center;
  /* Centre le texte */
  overflow: hidden;
  /* Empêche le débordement */
  text-overflow: ellipsis;
  /* Affiche "..." pour le contenu dépassant */
  white-space: nowrap;
  /* Empêche le retour à la ligne */
}

/* Première colonne figée */
.table-auto th:first-child,
.table-auto td:first-child {
  font-size: 11px;
  width: 260px;
  /* Largeur fixe */
  max-width: 260px;
  /* Limite la largeur */
  min-width: 260px;
  /* Limite la largeur */
  position: sticky;
  left: 0;
  z-index: 3;
  /* Assure que la colonne reste visible au-dessus des autres */
  background-color: #9ac3b9;
  background-color: #edf1f5;
  font-weight: bold;
  /* Vert clair */
  color: #456564;
  /* Texte gris foncé */
  box-shadow: 4px 0px 5px rgba(0, 0, 0, 0.1);
  /* Ombre à droite */
}

/* Dernière colonne figée */
.table-auto th:last-child,
.table-auto td:last-child {
  position: sticky;
  right: 0;
  z-index: 3;
  /* Assure que la colonne reste visible au-dessus des autres */
  background-color: #edf1f5;
  /* Vert clair */
  color: #2d3748;
  /* Texte gris foncé */
  font-weight: bold;
  box-shadow: -3px 0px 5px rgba(0, 0, 0, 0.1);
  /* Ombre à gauche */
}

/* Style personnalisé pour la première ligne (en-tête) de la première colonne */
.table-auto th:first-child {
  position: sticky;
  left: 0;
  z-index: 3;
  background-color: #2d3748;
  /* Vert plus foncé */
  color: #ddffe1;
  /* Texte gris foncé */
  padding: 5px 10px;
}

/* Style personnalisé pour la première ligne (en-tête) de la dernière colonne */
.table-auto th:last-child {
  position: sticky;
  left: 0;
  z-index: 3;
  background-color: #2d3748;
  /* Vert plus foncé */
  color: #fff;
  /* Texte gris foncé */
  padding: 5px 10px;
}

/* En-tête */
thead th {
  background-color: #2d3748;
  /* Noir/gris foncé */
  color: #fff;
  text-align: center;
  border-bottom: 2px solid #4a5568;
}

/* Cellules */
tbody td {
  border: 0.5px solid #e2e8f0;
  color: #000;
}

/* Ligne des totaux */
.bg-gray-100 {
  font-weight: bold;
  /*background-color: #9ac3b9;*/
  /* Vert clair */
}

.text-green-700 {
  background-color: #edf1f5;
  border-bottom: 1px solid #4a5568;
  box-shadow: 0px 3px 5px rgba(0, 0, 0, 0.11);
  /* Ombre en dessous */
  color: #486a5d;
}

.font-medium {
  font-weight: 500;
}

/* Survol des lignes */
tbody tr:hover {
  cursor: pointer;
  /*border: 2px dashed #4a5568;*/
  /* Bordure grise en pointillés */
}

/* Effet sur survol des colonnes */
tbody td:hover {
  cursor: pointer;
  /*border: 2px dashed #4a5568;*/
  /* Bordure grise en pointillés */
}

/* Alignement et mise en évidence */
td.text-left {
  text-align: left;
}

td.text-center {
  text-align: center;
}
</style>
